import { useState, useRef, useEffect, useCallback } from 'react';
import {
  useNotifications,
  UserAbility,
  createAssigneeOptions,
  getAssigneeIcon,
  outOfOfficeForAssignee,
} from '@medprompt/shared';
import { StatusEnum } from '../enums/statusEnum';

const UNASSIGNED = { userId: null, name: 'Unassigned', icon: null };

const useDocumentAssignee = ({ doc, user, activeUsers, teams, documentService, insightsService, updateDoc }) => {
  const { addNotification } = useNotifications();
  const [assignee, setAssignee] = useState(UNASSIGNED);
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);
  const [assignmentCounts, setAssignmentCounts] = useState(null);
  const [assignmentCountsLoading, setAssignmentCountsLoading] = useState(false);

  const docId = doc?.documentId;
  const docAssigneeId = doc?.assigneeId;
  const hasDocsWrite = Boolean(user?.authorities?.includes(UserAbility.DOCS_WRITE.key));
  const hasDocsReadAll = Boolean(user?.authorities?.includes(UserAbility.DOCS_READ_ALL.key));
  const canEditAssignee = hasDocsWrite && doc?.status !== StatusEnum.FILED_AWAY.value;

  const docIdRef = useRef(docId);
  useEffect(() => {
    docIdRef.current = docId;
  }, [docId]);

  // The toolbar is not rendered without a document, so the dropdown starts closed for each one.
  useEffect(() => {
    setAssigneeDropdownOpen(false);
    setAssignmentCounts(null);
    setAssignmentCountsLoading(false);
  }, [docId]);

  // Update assignee when activeUsers, teams, or doc.assigneeId changes
  useEffect(() => {
    if (!docId) return;
    if (docAssigneeId) {
      const foundUser = activeUsers.find(activeUser => activeUser.userId === docAssigneeId);
      if (foundUser) {
        setAssignee({ userId: foundUser.userId, name: foundUser.name, icon: null });
        return;
      }

      const foundTeam = teams.find(team => team.teamId === docAssigneeId);
      if (foundTeam) {
        setAssignee({ userId: foundTeam.teamId, name: foundTeam.teamName, icon: null, isTeam: true });
        return;
      }
    }
    setAssignee(UNASSIGNED);
  }, [docId, docAssigneeId, activeUsers, teams]);

  // Refetch assignment counts each time the dropdown opens (DOCS_READ_ALL only).
  useEffect(() => {
    if (!hasDocsReadAll || !assigneeDropdownOpen) {
      return undefined;
    }

    let cancelled = false;
    setAssignmentCounts(null);
    setAssignmentCountsLoading(true);

    const fetchCounts = async () => {
      try {
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const summary = await insightsService.fetchDocumentsAssigned(from, now);
        if (cancelled) return;

        const counts = {};
        summary?.documentsAssigned?.forEach(assignedDoc => {
          const assigneeId = assignedDoc.assigneeId || null;
          counts[assigneeId] = (counts[assigneeId] || 0) + 1;
        });

        setAssignmentCounts(counts);
        setAssignmentCountsLoading(false);
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching assignment counts:', error);
          setAssignmentCounts(null);
          setAssignmentCountsLoading(false);
        }
      }
    };

    fetchCounts();

    return () => {
      cancelled = true;
    };
  }, [hasDocsReadAll, assigneeDropdownOpen, insightsService]);

  const toggleAssigneeDropdown = useCallback(() => {
    if (!canEditAssignee) return;

    const nextOpen = !assigneeDropdownOpen;
    if (nextOpen && hasDocsReadAll) {
      setAssignmentCounts(null);
      setAssignmentCountsLoading(true);
    }
    setAssigneeDropdownOpen(nextOpen);
  }, [canEditAssignee, assigneeDropdownOpen, hasDocsReadAll]);

  const handleAssigneeChange = useCallback(async (assigneeOption) => {
    if (!canEditAssignee || !docId) return;
    const targetDocumentId = docId;

    const previousAssignee = assignee;
    setAssignee(assigneeOption);
    setAssigneeDropdownOpen(false);

    try {
      // Send '*' to backend when unassigning (userId is null)
      const userIdToSend = assigneeOption.userId === null ? '*' : assigneeOption.userId;
      await documentService.updateDocumentAssignee(targetDocumentId, userIdToSend);
      // Keep the document in step so a later refresh does not revert the dropdown, merging
      // into the current document rather than the one captured when the dropdown was clicked.
      updateDoc(prevDoc => (prevDoc?.documentId === targetDocumentId
        ? { ...prevDoc, assigneeId: assigneeOption.userId }
        : prevDoc));
    } catch (error) {
      console.error('Error updating assignee:', error);
      if (docIdRef.current === targetDocumentId) {
        setAssignee(previousAssignee);
      }
      addNotification(`assignee-error-${targetDocumentId}`, 'Failed to update assignee', { isError: true });
    }
  }, [canEditAssignee, docId, assignee, documentService, updateDoc, addNotification]);

  let assigneeOptions = createAssigneeOptions(
    activeUsers,
    teams,
    hasDocsReadAll && !assignmentCountsLoading ? assignmentCounts : null
  );
  if (hasDocsReadAll && assignmentCountsLoading) {
    assigneeOptions = assigneeOptions.map((option) => (
      option.id === 'unassigned' ? option : { ...option, display: `${option.name} (-)` }
    ));
  }

  return {
    assignee,
    selectedIcon: getAssigneeIcon(assignee.userId, activeUsers, teams),
    selectedOutOfOffice: assignee.isTeam ? null : outOfOfficeForAssignee(assignee.userId, activeUsers),
    assigneeOptions,
    assigneeDropdownOpen,
    toggleAssigneeDropdown,
    handleAssigneeChange,
    canEditAssignee,
  };
};

export default useDocumentAssignee;
